import React from 'react';

import {
  getEntityName,
  getRunIdentificationPair,
  listEntities,
  RunIdentificationPair,
} from '@neptune/shared/entity-domain';

import { knownAttributesForExperimentIdentification } from 'domain/experiment/attribute';

type UseResolveSelectedRunIdsParams = {
  isSelectedAsExperiment: (
    projectIdentifier: string,
    pairOrRunId: RunIdentificationPair | string,
  ) => boolean;
  isSelectedAsRun: (projectIdentifier: string, runId: string) => boolean;
};

type ResolveSelectedRunIdsResult = { runIds: string[]; experimentNames: string[] };
type ResolveExperimentNamesResult = { experimentNames: string[] };

const EMPTY_RESULT: string[] = [];

export function useResolveSelectedRunIds(): (
  projectIdentifier: string,
  selectedRunIds?: string[],
) => Promise<ResolveExperimentNamesResult>;
export function useResolveSelectedRunIds({
  isSelectedAsExperiment,
  isSelectedAsRun,
}: UseResolveSelectedRunIdsParams): (
  projectIdentifier: string,
  selectedRunIds?: string[],
) => Promise<ResolveSelectedRunIdsResult>;

export function useResolveSelectedRunIds(params?: UseResolveSelectedRunIdsParams) {
  return React.useCallback(
    async (projectIdentifier: string, selectedRunIds?: string[]) => {
      if (!selectedRunIds?.length) {
        return params
          ? { runIds: EMPTY_RESULT, experimentNames: EMPTY_RESULT }
          : { experimentNames: EMPTY_RESULT };
      }

      const entries = await listEntities(
        {
          projectIdentifier,
          runsIds: selectedRunIds,
          attributePathsFilter: knownAttributesForExperimentIdentification,
        },
        {
          filterArchived: false,
          flattenEntities: true,
        },
      );

      if (!params) {
        return { experimentNames: entries.map(getEntityName).filter(Boolean) };
      }

      const isSelectedAsExperiment = params?.isSelectedAsExperiment;
      const isSelectedAsRun = params?.isSelectedAsRun;

      const experimentNames: string[] = [];

      for (const entry of entries) {
        const runIdentificationPair = getRunIdentificationPair(entry);

        if (
          runIdentificationPair?.experimentName &&
          isSelectedAsExperiment(projectIdentifier, runIdentificationPair.runId)
        ) {
          experimentNames.push(runIdentificationPair.experimentName);
        }
      }

      return {
        experimentNames,
        runIds: selectedRunIds.filter((runId) => isSelectedAsRun(projectIdentifier, runId)),
      };
    },
    [params],
  );
}
